import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { AppLanguage, getInitialLanguage } from './core/state/languageState';

const initialLanguage = getInitialLanguage();

// Only the BUILD language's dictionary ships in the main chunk (spec 0008
// round 2): both JSONs together are ~325KB raw and one was always dead
// weight. `process.env.REACT_APP_LANG` is a build-time literal (CRA
// DefinePlugin), so webpack parses only the taken branch and drops the
// other file from the bundle entirely.
const buildLanguage: AppLanguage =
  process.env.REACT_APP_LANG === 'pl' ? 'pl' : 'ru';
const buildTranslation =
  process.env.REACT_APP_LANG === 'pl'
    ? // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
      require('./locales/pl/translation.json')
    : // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
      require('./locales/ru/translation.json');

const loadedLanguages = new Set<AppLanguage>([buildLanguage]);

/** Lazy-load a dictionary chunk and register it (no-op when already in). */
export const ensureLanguageLoaded = async (
  lang: AppLanguage,
): Promise<void> => {
  if (loadedLanguages.has(lang)) return;
  const dict =
    lang === 'pl'
      ? await import(
          /* webpackChunkName: "locale-pl" */ './locales/pl/translation.json'
        )
      : await import(
          /* webpackChunkName: "locale-ru" */ './locales/ru/translation.json'
        );
  i18n.addResourceBundle(
    lang,
    'translation',
    (dict as { default?: unknown }).default ?? dict,
    true,
    true,
  );
  loadedLanguages.add(lang);
};

i18n.use(initReactI18next).init({
  lng: initialLanguage,
  // The fallback must be a dictionary that is actually loaded — i.e. the
  // build language (the ru/pl files have key parity, locale tests enforce it).
  fallbackLng: buildLanguage,
  // Resources are provided synchronously / via addResourceBundle — never
  // let react-i18next suspend waiting for a backend.
  react: { useSuspense: false },
  resources: {
    [buildLanguage]: {
      translation: buildTranslation,
    },
  },
});

// A user may have switched to the non-build language earlier (localStorage
// boot override) — load their dictionary and flip as soon as it lands. In
// practice this resolves behind the bootstrap Splash.
if (initialLanguage !== buildLanguage) {
  ensureLanguageLoaded(initialLanguage)
    .then(() => i18n.changeLanguage(initialLanguage))
    .catch(() => {
      // Chunk failed to load (offline / stale deploy) — stay on the build
      // language rather than showing raw keys.
    });
}

export default i18n;
